import { coerceBlobForPreview } from '../../utils/attachmentPreview';
import { request, getBaseUrl, sessionFetch } from './http';

export async function getLibraryOfRecords(filters = {}) {
    const params = new URLSearchParams();
    const write = (key, value) => {
      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return;
      params.set(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
    };
    write('companies', filters.companies);
    write('sites', filters.sites);
    write('years', filters.years);
    write('states', filters.states);
    write('fields', filters.fields);
    write('aspects', filters.aspects);
    write('recordTypes', filters.recordTypes);
    write('search', filters.search);
    write('page', filters.page);
    write('limit', filters.limit);
    const query = params.toString();
    return request(`/data/library-of-records${query ? `?${query}` : ''}`);
  }

export async function downloadLibraryZip(filters = {}, filename = 'library-of-records.zip') {
    const response = await sessionFetch(`${getBaseUrl()}/api/data/library-of-records/download-zip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...filters, filename })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || `Zip download failed: ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  }

export async function fetchBlobForView(blobName, container) {
    const base = getBaseUrl() || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const endpoint = `/blob-storage/view-from-container/${encodeURIComponent(blobName)}?container=${encodeURIComponent(container)}`;
    const url = `${String(base).replace(/\/+$/, '')}/api${endpoint}`;
    const response = await sessionFetch(url, {
      method: 'GET',
      headers: {},
    });
    if (!response.ok) {
      const errText = await response.text();
      let message = `Failed to load file (${response.status})`;
      try {
        const j = JSON.parse(errText);
        if (j.message) message = j.message;
      } catch (_) {}
      throw new Error(message);
    }
    const raw = await response.blob();
    return coerceBlobForPreview(raw, blobName);
  }

export async function downloadFile(filename, container = null) {
    try {
      let endpoint;
      if (container) {
        // Use the download-from-container endpoint
        endpoint = `/blob-storage/download-from-container/${encodeURIComponent(filename)}?container=${encodeURIComponent(container)}`;
      } else {
        // Use the basic download endpoint
        endpoint = `/blob-storage/download/${encodeURIComponent(filename)}`;
      }
      
      const response = await sessionFetch(`${getBaseUrl()}/api${endpoint}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFilename = filename;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches) {
          downloadFilename = matches[1];
        }
      }
      
      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

export async function downloadFilesAsZip(files, filename = 'library-of-records.zip') {
    try {
      console.log('API Service - Starting zip download:', { files, filename });
      
      const response = await sessionFetch(`${getBaseUrl()}/api/blob-storage/download-zip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ files, filename })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Zip download failed:', response.status, errorText);
        throw new Error(`Zip download failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      console.log('API Service - Zip download response received, creating blob...');
      
      // Create blob and trigger download
      const blob = await response.blob();
      console.log('API Service - Blob created, size:', blob.size);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('API Service - Download triggered successfully');
      return { success: true };
    } catch (error) {
      console.error('Zip download error:', error);
      throw error;
    }
  }

export async function getEmailCatalog() {
    return request('/notifications/email-catalog');
  }

export async function getEmailCatalogSample(id) {
    return request(`/notifications/email-catalog/${encodeURIComponent(id)}`);
  }

export async function downloadEmailCatalogEml(id) {
    const endpoint = `/notifications/email-catalog/${encodeURIComponent(id)}/eml`;
    const response = await sessionFetch(`${getBaseUrl()}/api${endpoint}`, {
      method: 'GET'
    });
    if (!response.ok) {
      let message = `Download failed: ${response.statusText}`;
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (_) {
        // ignore
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${id}.eml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  }

export const fileManagementBlobStorageApi = {
  getLibraryOfRecords,
  downloadLibraryZip,
  fetchBlobForView,
  downloadFile,
  downloadFilesAsZip,
  getEmailCatalog,
  getEmailCatalogSample,
  downloadEmailCatalogEml,
};

